import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import PasswordResetPage from "@/pages/PasswordResetPage";
import PasswordResetConfirmPage from "@/pages/PasswordResetConfirmPage";
import CustomerDashboard from "@/pages/customer/Dashboard";
import CustomerRewards from "@/pages/customer/Rewards";
import CustomerHistory from "@/pages/customer/History";
import CustomerVouchers from "@/pages/customer/Vouchers";
import CustomerSettings from "@/pages/customer/Settings";
import CustomerIssues from "@/pages/customer/Issues";
import StaffDashboard from "@/pages/staff/Dashboard";
import StaffCustomers from "@/pages/staff/Customers";
import StaffCustomerDetail from "@/pages/staff/CustomerDetail";
import StaffVoucherValidation from "@/pages/staff/VoucherValidation";
import StaffRides from "@/pages/staff/Rides";
import StaffIssues from "@/pages/staff/Issues";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminRewards from "@/pages/admin/Rewards";
import AdminTiers from "@/pages/admin/Tiers";
import AdminUsers from "@/pages/admin/Users";
import AdminSettings from "@/pages/admin/Settings";
import AdminExports from "@/pages/admin/Exports";
import AdminUserActivity from "@/pages/admin/UserActivity";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// API Helper
export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await api.get("/auth/me");
          setUser(response.data.user);
          setLoyalty(response.data.loyalty);
          localStorage.setItem("token", response.data.access_token);
        } catch (e) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", response.data.access_token);
    setUser(response.data.user);
    setLoyalty(response.data.loyalty);
    return response.data;
  };

  const register = async (data) => {
    const response = await api.post("/auth/register", data);
    localStorage.setItem("token", response.data.access_token);
    setUser(response.data.user);
    setLoyalty(response.data.loyalty);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setLoyalty(null);
  };

  const refreshLoyalty = async () => {
    if (user?.role === 'customer') {
      const response = await api.get("/auth/me");
      setLoyalty(response.data.loyalty);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loyalty, loading, login, register, logout, refreshLoyalty }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles && !roles.includes(user.role)) {
    const redirectPath = user.role === 'customer' ? '/dashboard' : 
                         user.role === 'staff' ? '/staff' : '/admin';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/password-reset" element={<PasswordResetPage />} />
          <Route path="/password-reset/confirm" element={<PasswordResetConfirmPage />} />
          
          {/* Customer Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute roles={['customer']}><CustomerDashboard /></ProtectedRoute>
          } />
          <Route path="/rewards" element={
            <ProtectedRoute roles={['customer']}><CustomerRewards /></ProtectedRoute>
          } />
          <Route path="/history" element={
            <ProtectedRoute roles={['customer']}><CustomerHistory /></ProtectedRoute>
          } />
          <Route path="/vouchers" element={
            <ProtectedRoute roles={['customer']}><CustomerVouchers /></ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute roles={['customer']}><CustomerSettings /></ProtectedRoute>
          } />
          <Route path="/issues" element={
            <ProtectedRoute roles={['customer']}><CustomerIssues /></ProtectedRoute>
          } />
          
          {/* Staff Routes */}
          <Route path="/staff" element={
            <ProtectedRoute roles={['staff', 'admin']}><StaffDashboard /></ProtectedRoute>
          } />
          <Route path="/staff/customers" element={
            <ProtectedRoute roles={['staff', 'admin']}><StaffCustomers /></ProtectedRoute>
          } />
          <Route path="/staff/customers/:id" element={
            <ProtectedRoute roles={['staff', 'admin']}><StaffCustomerDetail /></ProtectedRoute>
          } />
          <Route path="/staff/vouchers" element={
            <ProtectedRoute roles={['staff', 'admin']}><StaffVoucherValidation /></ProtectedRoute>
          } />
          <Route path="/staff/rides" element={
            <ProtectedRoute roles={['staff', 'admin']}><StaffRides /></ProtectedRoute>
          } />
          <Route path="/staff/issues" element={
            <ProtectedRoute roles={['staff', 'admin']}><StaffIssues /></ProtectedRoute>
          } />
          
          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>
          } />
          <Route path="/admin/rewards" element={
            <ProtectedRoute roles={['admin']}><AdminRewards /></ProtectedRoute>
          } />
          <Route path="/admin/tiers" element={
            <ProtectedRoute roles={['admin']}><AdminTiers /></ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute roles={['admin']}><AdminUsers /></ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute roles={['admin']}><AdminSettings /></ProtectedRoute>
          } />
          <Route path="/admin/exports" element={
            <ProtectedRoute roles={['admin']}><AdminExports /></ProtectedRoute>
          } />
          <Route path="/admin/activity" element={
            <ProtectedRoute roles={['admin']}><AdminUserActivity /></ProtectedRoute>
          } />
          
          {/* Default Redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
